/* eslint-disable arrow-body-style */
import {Actions, createEffect, ofType} from "@ngrx/effects";
import {inject} from "@angular/core";
import {
    CopyFundReportAndValuationsActions,
    FundValuationActions,
    FundValuationDeferralActions,
    FundValuationOverviewActions,
    FundWeightedCashflowBreakdownActions,
    ReturnSummaryActions,
    TopDownValuationActions,
    UndrawnValuationActions
} from "../fund.actions";
import {catchError, debounceTime, exhaustMap, filter, forkJoin, from, map, of, switchMap} from "rxjs";
import {FundValuationService} from "../../services/fund-valuation.service";
import {Store} from "@ngrx/store";
import {
    selectFundValuationOverviewSearchParams,
    selectSelectedFundValuation,
    selectSelectedFundValuationId,
    selectSelectedFundValuationScenarios
} from "./fund-valuation.selectors";
import {selectFundContext, selectSelectedFundId} from "../fund/fund.selectors";
import {FundValuationWithScenarios} from "../../models/fund-valuation-with-scenarios";
import {tap} from "rxjs/operators";
import {ActivatedRoute, Router} from "@angular/router";
import {selectSelectedFundReportId} from "../fund-report/fund-report.selectors";
import {EMPTY_FUND_VALUATION} from "../../models/fund-valuation";
import {HttpResponseError} from "../../../shared/model/http-response-error";
import {ErrorHandlingUtil} from "../../../shared/utils/error-handling-util";
import {summarizeBulkResults, toBulkResult} from "../../../shared/utils/bulk-operation-util";
import {MatSnackBar} from "@angular/material/snack-bar";
import {concatLatestFrom} from "@ngrx/operators";
import {MatDialog} from "@angular/material/dialog";
import {PatternAssignmentComponent} from "../../../shared/components/pattern-group/pattern-assignment/pattern-assignment.component";
import {selectSelectedSourceDataset} from "../../../import/store/import.selectors";
import {PatternActions} from "../../../shared/store/shared.actions";

// #########################################
// FUND Valuation Overview Search (server-side pagination)
// #########################################
export const triggerFundValuationOverviewSearchOnParamsChange = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(
                FundValuationOverviewActions.changePage,
                FundValuationOverviewActions.changeSort,
                FundValuationOverviewActions.changeSearch
            ),
            map(() => FundValuationOverviewActions.search())
        );
    },
    {functional: true}
);

export const searchFundValuationsOverview = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), fundValuationService = inject(FundValuationService)) => {
        return actions$.pipe(
            ofType(FundValuationOverviewActions.search),
            concatLatestFrom(() => store$.select(selectFundValuationOverviewSearchParams)),
            switchMap(([, params]) =>
                fundValuationService.searchFundValuationsOverview(params).pipe(
                    map((result) => FundValuationOverviewActions.searchSuccess({result})),
                    catchError((errorResponse: any) => of(FundValuationOverviewActions.searchError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

// When a search lands on a page past the end (e.g. after archiving every row on the last page),
// the backend returns empty content with a number past totalPages. Clamp back to the last real page.
export const clampFundValuationOverviewPageWhenOutOfRange = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(FundValuationOverviewActions.searchSuccess),
            filter(({result}) => result.content.length === 0 && result.totalElements > 0 && result.number > result.totalPages - 1),
            map(({result}) => FundValuationOverviewActions.changePage({page: result.totalPages - 1, size: result.size}))
        );
    },
    {functional: true}
);

export const deleteFundValuationFromOverview = createEffect(
    (actions$ = inject(Actions), fundValuationService = inject(FundValuationService)) => {
        return actions$.pipe(
            ofType(FundValuationOverviewActions.deleteValuation),
            exhaustMap(action =>
                fundValuationService.deleteFundValuation(action.fundId, action.fundReportId, action.fundValuationId).pipe(
                    map(() => FundValuationOverviewActions.deleteValuationSuccess()),
                    catchError((errorResponse: any) => of(FundValuationOverviewActions.deleteValuationError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                ))
        );
    },
    {functional: true}
);

export const reloadFundValuationsAfterDelete = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(FundValuationOverviewActions.deleteValuationSuccess),
            map(() => FundValuationOverviewActions.search())
        );
    },
    {functional: true}
);

export const showDeleteFundValuationFromOverviewSuccess = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(FundValuationOverviewActions.deleteValuationSuccess),
            tap(() => snackBar.open("Fund valuation deleted.", "", {duration: 3000}))
        );
    },
    {functional: true, dispatch: false}
);

export const showDeleteFundValuationFromOverviewError = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(FundValuationOverviewActions.deleteValuationError),
            tap((action) => snackBar.open("Fund valuation could not be deleted. " + action.errorMsg, "", {duration: 3000}))
        );
    },
    {functional: true, dispatch: false}
);

export const deleteFundValuationsFromOverview = createEffect(
    (actions$ = inject(Actions), fundValuationService = inject(FundValuationService)) => {
        return actions$.pipe(
            ofType(FundValuationOverviewActions.deleteValuations),
            exhaustMap((action) => {
                if (action.valuations.length === 0) {
                    return of(FundValuationOverviewActions.deleteValuationsError({errorMsg: "No valuations selected"}));
                }
                return forkJoin(
                    action.valuations.map((valuation) =>
                        toBulkResult(fundValuationService.deleteFundValuation(valuation.fundId, valuation.fundReportId, valuation.fundValuationId)))
                ).pipe(
                    map((results) => {
                        const {count, failed, firstError} = summarizeBulkResults(results);
                        return count === 0
                            ? FundValuationOverviewActions.deleteValuationsError({errorMsg: firstError ?? "Valuations could not be deleted."})
                            : FundValuationOverviewActions.deleteValuationsSuccess({count, failed});
                    })
                );
            })
        );
    },
    {functional: true}
);

export const reloadFundValuationsAfterBulkDelete = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(FundValuationOverviewActions.deleteValuationsSuccess),
            map(() => FundValuationOverviewActions.search())
        );
    },
    {functional: true}
);

export const showDeleteFundValuationsSuccess = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(FundValuationOverviewActions.deleteValuationsSuccess),
            tap((action) => {
                const noun = action.count === 1 ? "valuation" : "valuations";
                const failedSuffix = action.failed > 0 ? ` ${action.failed} failed.` : "";
                snackBar.open(`${action.count} ${noun} deleted.${failedSuffix}`, "", {duration: 3000});
            })
        );
    },
    {functional: true, dispatch: false}
);

export const showDeleteFundValuationsError = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(FundValuationOverviewActions.deleteValuationsError),
            tap((action) => snackBar.open("Valuations could not be deleted. " + action.errorMsg, "", {duration: 3000}))
        );
    },
    {functional: true, dispatch: false}
);

// #########################################
// FUND Valuation Create
// #########################################
export const createFundValuation = createEffect(
    (actions$ = inject(Actions), router$ = inject(Router), fundValuationService = inject(FundValuationService)) => {
        return actions$.pipe(
            ofType(FundValuationActions.create),
            exhaustMap((action) => {
                if (action.fundId) {
                    return fundValuationService.createFundValuation(
                        action.fundId,
                        action.fundReportId,
                        {
                            ...EMPTY_FUND_VALUATION,
                            closingDate: {date: action.closingDate},
                            description: {text: action.description},
                            valuationType: {code: action.valuationType}
                        },
                        action.templateId
                    ).pipe(
                        map((fundValuationWithScenarios) => FundValuationActions.createSuccess({fundValuationWithScenarios})),
                        tap((loadedAction) => {
                            if (loadedAction.type === FundValuationActions.createSuccess.type) {
                                router$.navigate([], {
                                    queryParams: {fundValuationId: loadedAction.fundValuationWithScenarios.fundValuation.id, dealId: null}, // null removes it
                                    queryParamsHandling: "merge",
                                });
                            }
                        }),
                        catchError((errorResponse: any) => of(FundValuationActions.loadError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                    );
                } else {
                    return of(FundValuationActions.createError({errorMsg: "Fund could not be found"}));
                }
            })
        );
    },
    {functional: true}
);

export const showCreateSuccess = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar), dialog = inject(MatDialog)) => {
        return actions$.pipe(
            ofType(FundValuationActions.createSuccess),
            tap(() => {
                dialog.closeAll();
                snackBar.open("Fund valuation created successfully.", "", {
                    duration: 3000
                });
            })
        );
    },
    {functional: true, dispatch: false}
);

// #########################################
// FUND Valuation SAVE
// #########################################
export const saveFundValuation = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), fundValuationService = inject(FundValuationService)) => {
        return actions$.pipe(
            ofType(FundValuationActions.save),
            concatLatestFrom(() => [store$.select(selectFundContext), store$.select(selectSelectedSourceDataset)]),
            exhaustMap(([action, fundContext, dataset]) => {
                if (fundContext.fundId === undefined || fundContext.fundReportId === undefined || fundContext.fundValuationId === undefined) {
                    return of(FundValuationActions.saveError({errorMsg: "Fund, Fund Report or Fund Valuation could not be found"}));
                }
                return fundValuationService.updateFundValuation(
                    fundContext.fundId,
                    fundContext.fundReportId,
                    fundContext.fundValuationId,
                    action.fundValuation,
                    action.scenarios,
                    fundContext.dealId,
                    dataset
                ).pipe(
                    map((fundData) => FundValuationActions.saveSuccess({fundData})),
                    catchError((errorResponse: any) => of(FundValuationActions.saveError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                );
            })
        );
    },
    {functional: true}
);

// #########################################
// FUND Valuation DELETE
// #########################################
export const deleteFundValuation = createEffect(
    (actions$ = inject(Actions), fundValuationService = inject(FundValuationService)) => {
        return actions$.pipe(
            ofType(FundValuationActions.delete),
            exhaustMap(action => {
                return fundValuationService.deleteFundValuation(action.fundId, action.fundReportId, action.fundValuationId).pipe(
                    map(() => FundValuationActions.deleteSuccess({
                        fundId: action.fundId,
                        fundReportId: action.fundReportId,
                        fundValuationId: action.fundValuationId
                    })),
                    catchError((errorResponse: any) => {
                        const httpResponseError: HttpResponseError = {
                            statusCode: errorResponse.status,
                            errorMessage: ErrorHandlingUtil.getErrorMessage(errorResponse)
                        }
                        return of(FundValuationActions.deleteError({httpResponseError}));
                    })
                );
            })
        );
    },
    {functional: true}
);

export const cleanupQueryParamsAfterDeleteAndReloadValuation = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), router$ = inject(Router), route = inject(ActivatedRoute)) => {
        return actions$.pipe(
            ofType(FundValuationActions.deleteSuccess),
            concatLatestFrom(() => store$.select(selectSelectedFundValuationId)),
            tap(() => {
                const queryParams = {...route.snapshot.queryParams};
                delete queryParams["fundValuationId"];
                router$.navigate([], {relativeTo: route, queryParams, replaceUrl: true});
            })
        );
    },
    {functional: true, dispatch: false}
);

export const showDeleteSuccess = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(FundValuationActions.deleteSuccess),
            tap(() => {
                snackBar.open("Fund valuation deleted successfully.", "", {
                    duration: 3000
                });
            })
        );
    },
    {functional: true, dispatch: false}
);

export const saveFundValuationDescription = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), fundValuationService = inject(FundValuationService)) => {
        return actions$.pipe(
            ofType(FundValuationActions.saveDescription),
            concatLatestFrom(() => store$.select(selectSelectedFundValuationId)),
            exhaustMap(([action, fundValuationId]) => {
                if (fundValuationId) {
                    const descriptionRequest = {
                        description: action.description
                    }
                    return fundValuationService.updateFundValuationDescription(
                        fundValuationId,
                        descriptionRequest
                    ).pipe(
                        map((description) => FundValuationActions.saveDescriptionSuccess({description})),
                        catchError((errorResponse: any) => of(FundValuationActions.saveDescriptionError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                    );
                } else {
                    return of(FundValuationActions.saveDescriptionError({errorMsg: "Fund Valuation could not be found"}));
                }
            })
        );
    },
    {functional: true}
);

export const saveManagementFeeSettings = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), fundValuationService = inject(FundValuationService)) => {
        return actions$.pipe(
            ofType(FundValuationActions.saveManagementFeeSettings),
            concatLatestFrom(() => store$.select(selectFundContext)),
            exhaustMap(([action, ctx]) => {
                if (ctx.fundId && ctx.fundReportId && ctx.fundValuationId) {
                    const managementFeeScaleRequest = {
                        managementFeeScale: action.managementFeeScale
                    }
                    return fundValuationService.updateFundValuationManagementFeeScale(
                        ctx.fundId,
                        ctx.fundReportId,
                        ctx.fundValuationId,
                        managementFeeScaleRequest
                    ).pipe(
                        map((fundValuationWithScnsAndAggrCFs) => FundValuationActions.saveManagementFeeSettingsSuccess({fundValuationWithScnsAndAggrCFs})),
                        catchError((errorResponse: any) => of(FundValuationActions.saveManagementFeeSettingsError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                    );
                } else {
                    return of(FundValuationActions.saveManagementFeeSettingsError({errorMsg: "Fund, Fund Report or Fund Valuation could not be found"}));
                }
            })
        );
    },
    {functional: true}
);

export const calcUndrawnValuation = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), fundValuationService = inject(FundValuationService)) => {
        return actions$.pipe(
            ofType(UndrawnValuationActions.calc),
            concatLatestFrom(() => [store$.select(selectSelectedFundId), store$.select(selectSelectedFundValuationId)]),
            exhaustMap(([action, fundId, fundValuationId]) => {
                if (fundId === undefined || fundValuationId === undefined) {
                    return of(UndrawnValuationActions.calcError({errorMsg: "No valuation for this fund available, please add a valuation first"}));
                }
                const fundValuationWithScenarios: FundValuationWithScenarios = {
                    fundValuation: action.fundValuation,
                    scenarios: action.scenarios
                };
                return fundValuationService.calcUndrawnValuation(fundId, fundValuationId, fundValuationWithScenarios).pipe(
                    map((fvWithScenarios) => UndrawnValuationActions.calcSuccess({fundValuation: fvWithScenarios.fundValuation, scenarios: fvWithScenarios.scenarios})),
                    catchError((error: any) =>
                        of(UndrawnValuationActions.calcError({errorMsg: ErrorHandlingUtil.getErrorMessage(error)}))
                    )
                );
            })
        );
    },
    {functional: true}
);

export const applyUnfundedParameters = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), fundValuationService = inject(FundValuationService)) => {
        return actions$.pipe(
            ofType(UndrawnValuationActions.applyUnfundedParams),
            concatLatestFrom(() => [store$.select(selectSelectedFundId), store$.select(selectSelectedFundValuationId)]),
            exhaustMap(([action, fundId, fundValuationId]) => {
                if (fundId === undefined || fundValuationId === undefined) {
                    return of(UndrawnValuationActions.calcError({errorMsg: "No valuation for this fund available, please add a valuation first"}));
                }
                const fundValuationWithScenarios: FundValuationWithScenarios = {
                    fundValuation: action.fundValuation,
                    scenarios: action.scenarios
                };
                return fundValuationService.applyUnfundedParameters(fundId, fundValuationId, fundValuationWithScenarios).pipe(
                    map((fvWithScenarios) => UndrawnValuationActions.calcSuccess({fundValuation: fvWithScenarios.fundValuation, scenarios: fvWithScenarios.scenarios})),
                    catchError((error: any) =>
                        of(UndrawnValuationActions.calcError({errorMsg: ErrorHandlingUtil.getErrorMessage(error)}))
                    )
                );
            })
        );
    },
    {functional: true}
);

// #########################################
// RETURN SUMMARY PREVIEW
// #########################################
export const calcReturnSummaryPreview = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), fundValuationService = inject(FundValuationService)) => {
        return actions$.pipe(
            ofType(ReturnSummaryActions.calc),
            debounceTime(300),
            concatLatestFrom(() => [store$.select(selectSelectedFundId), store$.select(selectSelectedFundValuationId)]),
            switchMap(([action, fundId, fundValuationId]) => {
                if (fundId === undefined || fundValuationId === undefined) {
                    return of(ReturnSummaryActions.calcError({errorMsg: "No valuation for this fund available, please add a valuation first"}));
                }
                return fundValuationService.previewReturnSummary(fundId, fundValuationId, {
                    bidPriceAdjustment: action.bidPriceAdjustment,
                    discountToNAV: action.discountToNAV,
                    editedField: action.editedField
                }).pipe(
                    map((fundValuationWithScenarios) => ReturnSummaryActions.calcSuccess({fundValuationWithScenarios})),
                    catchError((errorResponse: any) => of(ReturnSummaryActions.calcError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                );
            })
        );
    },
    {functional: true}
);

export const loadFundValuationsForCopyWorkflow = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), fundValuationService = inject(FundValuationService)) => {
        return actions$.pipe(
            ofType(CopyFundReportAndValuationsActions.loadFundValuations),
            exhaustMap((action) =>
                fundValuationService.getValuationsForFundReport(action.fundId, action.fundReportId).pipe(
                    map((fundValuations) => CopyFundReportAndValuationsActions.loadFundValuationsSuccess({fundValuations})),
                    catchError((errorResponse: any) => of(CopyFundReportAndValuationsActions.loadFundValuationsError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

export const loadAssetValuationsSelectionForCopyWorkflow = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), fundValuationService = inject(FundValuationService)) => {
        return actions$.pipe(
            ofType(CopyFundReportAndValuationsActions.loadAssetValuationsSelection),
            switchMap((action) =>
                fundValuationService.getAssetValuationsForCopyWorkflow(action.fundId, action.fundReportId, action.fundValuationId, action.copyConfig).pipe(
                    map((assetValuationsSelection) => CopyFundReportAndValuationsActions.loadAssetValuationsSelectionSuccess({assetValuationsSelection})),
                    catchError((errorResponse: any) => of(
                        CopyFundReportAndValuationsActions.loadAssetValuationsSelectionError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)}))
                    )
                )
            )
        );
    },
    {functional: true}
);

export const assignPatternsOnFundValuation = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), fundValuationService = inject(FundValuationService)) => {
        return actions$.pipe(
            ofType(FundValuationActions.assignPatterns),
            concatLatestFrom(() => [store$.select(selectSelectedFundId), store$.select(selectSelectedFundReportId)]),
            exhaustMap(([action, fundId, fundReportId]) => {
                    if (!fundId) {
                        return of(FundValuationActions.loadError({errorMsg: "Fund could not be found"}));
                    } else if (!fundReportId) {
                        return of(FundValuationActions.loadError({errorMsg: "FundReport could not be found"}));
                    }
                    return fundValuationService.assignPatterns(fundId, fundReportId, action.fundValuationId, action.patternAssignment).pipe(
                        map((fundValuationWithScnsAndAggrCFs) => FundValuationActions.assignPatternsSuccess({fundValuationWithScnsAndAggrCFs})),
                        catchError((errorResponse: any) => {
                            const httpResponseError: HttpResponseError = {
                                statusCode: errorResponse.status,
                                errorMessage: ErrorHandlingUtil.getErrorMessage(errorResponse)
                            }
                            return of(FundValuationActions.assignPatternsError({httpResponseError}));
                        })
                    )
                }
            )
        );
    },
    {functional: true}
);

export const showPatternAssignmentSuccess = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(FundValuationActions.assignPatternsSuccess),
            tap(() => {
                snackBar.open("Pattern assignment successfully updated.", "", {duration: 3000});
            })
        );
    },
    {functional: true, dispatch: false}
);

export const reOpenPatternAssignmentDialogOnError = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), dialog = inject(MatDialog)) => {
        return actions$.pipe(
            ofType(FundValuationActions.assignPatternsError),
            concatLatestFrom(() => store$.select(selectSelectedFundValuation)),
            tap(([, fundValuation]) => {
                const dialogRef = dialog.open(PatternAssignmentComponent);
                dialogRef.componentInstance.fundValuation = fundValuation.data
            })
        );
    },
    {functional: true, dispatch: false}
);

export const savePatternWithRecalc = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), fundValuationService = inject(FundValuationService)) => {
        return actions$.pipe(
            ofType(PatternActions.save),
            filter(action => !!action.fundValuationId),
            concatLatestFrom(() => [store$.select(selectSelectedFundId), store$.select(selectSelectedFundReportId)]),
            exhaustMap(([action, fundId, fundReportId]) => {
                if (!fundId || !fundReportId || !action.fundValuationId || !action.pattern.id) {
                    return of(FundValuationActions.recalculateCashflowsError({errorMsg: "Missing fund context for pattern save and recalculation"}));
                }
                return fundValuationService.updatePatternForFundValuation(fundId, fundReportId, action.fundValuationId, action.pattern.id, action.pattern).pipe(
                    switchMap(result => from([
                        PatternActions.saveSuccess({pattern: result.pattern}),
                        FundValuationActions.recalculateCashflowsSuccess({fundValuationWithScnsAndAggrCFs: result.fundValuationWithScnsAndAggrCFs})
                    ])),
                    catchError((errorResponse: any) => of(FundValuationActions.recalculateCashflowsError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                );
            })
        );
    },
    {functional: true}
);

export const applyTemplateGroup = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), fundValuationService = inject(FundValuationService)) => {
        return actions$.pipe(
            ofType(FundValuationActions.applyTemplateGroup),
            concatLatestFrom(() => [store$.select(selectSelectedFundId), store$.select(selectSelectedFundReportId)]),
            exhaustMap(([action, fundId, fundReportId]) => {
                    if (!fundId) {
                        return of(FundValuationActions.loadError({errorMsg: "Fund could not be found"}));
                    } else if (!fundReportId) {
                        return of(FundValuationActions.loadError({errorMsg: "FundReport could not be found"}));
                    }
                    return fundValuationService.applyTemplateGroup(fundId, fundReportId, action.fundValuationId, action.templateId).pipe(
                        map((fundValuationWithScnsAndAggrCFs) => FundValuationActions.applyTemplateGroupSuccess({fundValuationWithScnsAndAggrCFs})),
                        catchError((errorResponse: any) => {
                            const httpResponseError: HttpResponseError = {
                                statusCode: errorResponse.status,
                                errorMessage: ErrorHandlingUtil.getErrorMessage(errorResponse)
                            }
                            return of(FundValuationActions.applyTemplateGroupError({httpResponseError}));
                        })
                    )
                }
            )
        );
    },
    {functional: true}
);

export const showApplyTemplateGroupSuccess = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(FundValuationActions.applyTemplateGroupSuccess),
            tap(() => {
                snackBar.open("Template group successfully applied.", "", {duration: 3000});
            })
        );
    },
    {functional: true, dispatch: false}
);

export const calcTopDownValuation = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), fundValuationService = inject(FundValuationService)) => {
        return actions$.pipe(
            ofType(TopDownValuationActions.calc),
            concatLatestFrom(() => [store$.select(selectSelectedFundId), store$.select(selectSelectedFundValuationId)]),
            // WI 1077: switchMap (not exhaustMap) so the latest edit wins — a recalc dispatched while a
            // previous one is in flight cancels the stale request instead of being dropped. Single
            // valuation, so there is no cross-scenario cancellation concern; the latest calc always
            // completes, so the component's in-flight flag is never left stuck.
            switchMap(([action, fundId, fundValuationId]) => {
                if (fundId === undefined || fundValuationId === undefined) {
                    return of(TopDownValuationActions.calcError({errorMsg: "No valuation for this fund available, please add a valuation first"}));
                }

                return fundValuationService.calcTopDownValuation(fundId, fundValuationId, action.topDownRequest).pipe(
                    map((fundValuationWithScenarios) => TopDownValuationActions.calcSuccess({fundValuationWithScenarios})),
                    catchError((errorResponse: any) => of(TopDownValuationActions.calcError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                );
            })
        );
    },
    {functional: true}
);

// #########################################
// NCA
// #########################################

export const saveNCA = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), fundService = inject(FundValuationService)) => {
        return actions$.pipe(
            ofType(FundValuationActions.saveNca),
            concatLatestFrom(() => [
                store$.select(selectFundContext),
                store$.select(selectSelectedSourceDataset)
            ]),
            exhaustMap(([action, ctx, dataset]) => {
                if (ctx.fundId && ctx.fundReportId && ctx.fundValuationId) {
                    return fundService.updateFundValuationScenariosNca(
                        ctx.fundId,
                        ctx.fundReportId,
                        ctx.fundValuationId,
                        action.exitDate,
                        action.lowNca,
                        action.baseNca,
                        action.highNca,
                        ctx.dealId,
                        dataset
                    ).pipe(
                        map((fundData) => FundValuationActions.saveNcaSuccess({fundData})),
                        catchError((errorResponse: any) => of(FundValuationActions.saveNcaError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                    );
                } else {
                    return of(FundValuationActions.saveNcaError({errorMsg: "Saving Net Current Assets Forecast Error: Fund, FundReport or FundValuation could not be found"}));
                }
            })
        );
    },
    {functional: true}
);


export const saveNcaSuccessInfo = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(FundValuationActions.saveNcaSuccess),
            tap(() => {
                snackBar.open("Net Current Assets Forecast Successfully saved.", "", {duration: 3000});
            })
        );
    },
    {functional: true, dispatch: false}
);

// #########################################
// Fund Valuation Deferral
// #########################################

export const loadFundValuationDeferralData = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), fundValuationService = inject(FundValuationService)) => {
        return actions$.pipe(
            ofType(FundValuationDeferralActions.load),
            concatLatestFrom(() => store$.select(selectFundContext)),
            exhaustMap(([, ctx]) => {
                    if (!ctx.fundId || !ctx.fundValuationId) {
                        return of(FundValuationDeferralActions.loadError({errorMsg: "Fund or Fund Valuation could not be found"}));
                    }
                    return fundValuationService.getDeferralsForFundValuation(ctx.fundId, ctx.fundValuationId).pipe(
                        map((fundValuationDeferralData) => FundValuationDeferralActions.loadSuccess({fundValuationDeferralData})),
                        catchError((errorResponse: any) => of(
                            FundValuationDeferralActions.loadError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)}))
                        )
                    )
                }
            )
        );
    },
    {functional: true}
);

export const saveFundValuationDeferralData = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), fundValuationService = inject(FundValuationService)) => {
        return actions$.pipe(
            ofType(FundValuationDeferralActions.save),
            concatLatestFrom(() => store$.select(selectFundContext)),
            exhaustMap(([action, ctx]) => {
                    if (!ctx.fundId || !ctx.fundValuationId) {
                        return of(FundValuationDeferralActions.saveError({errorMsg: "Fund or Fund Valuation could not be found"}));
                    }
                    return fundValuationService.upsertFundValuationDeferrals(ctx.fundId, ctx.fundValuationId, action.fundValuationDeferralData).pipe(
                        map((fundValuationDeferralData) => FundValuationDeferralActions.saveSuccess({fundValuationDeferralData})),
                        catchError((errorResponse: any) => of(
                            FundValuationDeferralActions.saveError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)}))
                        )
                    )
                }
            )
        );
    },
    {functional: true}
);

export const saveFundValuationDeferralSuccessInfo = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(FundValuationDeferralActions.saveSuccess),
            tap(() => {
                snackBar.open("Purchase Price Allocation Successfully saved.", "", {duration: 3000});
            })
        );
    },
    {functional: true, dispatch: false}
);


export const triggerFundValuationAfterDeferralSaveSuccess = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(FundValuationDeferralActions.saveSuccess),
            concatLatestFrom(() => store$.select(selectSelectedFundValuationScenarios)),
            map(([action, scenarios]) => action.fundValuationDeferralData.fundValuation
                ? FundValuationActions.save({fundValuation: action.fundValuationDeferralData.fundValuation, scenarios})
                : FundValuationDeferralActions.saveError({errorMsg: "Fund valuation update and calc not possible"}))
        );
    },
    {functional: true}
);

// #########################################
// Return Summary - Goal Seek
// #########################################

export const performFundGoalSeek = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), fundValuationService = inject(FundValuationService)) => {
        return actions$.pipe(
            ofType(ReturnSummaryActions.goalSeek),
            concatLatestFrom(() => store$.select(selectFundContext)),
            exhaustMap(([action, ctx]) => {
                if (!ctx.fundId || !ctx.fundValuationId) {
                    return of(ReturnSummaryActions.goalSeekError({errorMsg: "Fund or Fund Valuation could not be found"}));
                }
                return fundValuationService.goalSeek(ctx.fundId, ctx.fundValuationId, {
                    targetTvpi: action.targetTvpi,
                    targetTvpiActive: action.targetTvpiActive,
                    targetIrr: action.targetIrr,
                    targetIrrActive: action.targetIrrActive,
                    targetDiscount: action.targetDiscount,
                    targetDiscountActive: action.targetDiscountActive,
                }).pipe(
                    map((fundValuationWithScenarios) => ReturnSummaryActions.goalSeekComplete({fundValuationWithScenarios})),
                    catchError((errorResponse: any) => of(ReturnSummaryActions.goalSeekError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                );
            })
        );
    },
    {functional: true}
);

export const goalSeekCompleteInfo = createEffect(
    (actions$ = inject(Actions), dialog = inject(MatDialog)) => {
        return actions$.pipe(
            ofType(ReturnSummaryActions.goalSeekComplete),
            tap(() => dialog.closeAll())
        );
    },
    {functional: true, dispatch: false}
);

export const goalSeekErrorInfo = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(ReturnSummaryActions.goalSeekError),
            tap(({errorMsg}) => snackBar.open(errorMsg, "", {duration: 5000}))
        );
    },
    {functional: true, dispatch: false}
);

export const loadFundWeightedCashflowBreakdown = createEffect(
    (actions$ = inject(Actions), fundValuationService = inject(FundValuationService)) => actions$.pipe(
        ofType(FundWeightedCashflowBreakdownActions.load),
        switchMap((action) =>
            fundValuationService.getWeightedCashflowBreakdown(action.fundValuationId, action.date, action.cashflowTypes).pipe(
                map((breakdown) => FundWeightedCashflowBreakdownActions.loadSuccess({breakdown})),
                catchError((error: any) => of(FundWeightedCashflowBreakdownActions.loadError({
                    errorMsg: ErrorHandlingUtil.getErrorMessage(error)
                })))
            )
        )
    ),
    {functional: true}
);
